import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import BaseModal from '../../BaseModal';
import {
  clearPerformanceModalFilters,
  setPerformanceModals,
} from '@/store/slices/components/dashboard/DashboardSlice';
import NewContactsTable from '@/components/Dashboard/DashboardTables/NewContactsTable';
import buildQuery from '@/components/Dashboard/utils/buildQuery';
import { getPerformanceNewContactsTable } from '@/store/slices/components/dashboard/service';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import DownloadButton from '@/components/Dashboard/DashboardTables/components/DownloadButton';
import styles from '../../performanceModals.module.scss';
import TableDropdown from '@/components/Dashboard/DashboardTables/components/TableDropdown';
import buildTableQuery from '@/components/Dashboard/utils/buildTableQuery';
import useAsyncTableExcelDownload from '@/components/Dashboard/customHooks/useAsyncTableExcelDownload';
import { TABLES } from '@/components/Dashboard/constants';
import { contactStatusOptions, contactTypesOptions } from '../utils';
import TableSearchEmptyState from '@/components/Dashboard/DashboardTables/TableSearchEmptyState';
import SearchInput from '../../SearchInput';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function NewContactsModal() {
  const dispatch = useDispatch();
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { newContacts } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const {
    list,
    loading,
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
  } = newContacts;
  const track = useTrackPerformanceTable();
  const { handleDownloadExcel: downloadExcel, isDownloading } =
    useAsyncTableExcelDownload(TABLES.NEW_CONTACTS);

  useEffect(() => {
    const fetchData = () => {
      const query = buildQuery(selectedIds);
      const tableQuery = buildTableQuery(newContacts);

      dispatch(getPerformanceNewContactsTable({ query, tableQuery }));
    };

    fetchData();
  }, [sortBy, sortDirection, searchQuery, searchTypeFlag, searchStatus]);

  const handleDownloadExcel = () => {
    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(newContacts);
    downloadExcel(query, tableQuery);
    track.newContacts(TRACK_ELEMENT.DOWNLOAD_BUTTON);
  };

  const handleClearFilters = () => {
    const modal = 'newContacts';
    const filters = ['searchQuery', 'searchType', 'searchStatus'];
    dispatch(clearPerformanceModalFilters({ modal, filters }));
  };

  const selectedStatusOption =
    contactStatusOptions.find((option) => option.value === searchStatus) ??
    contactStatusOptions[0];

  const handleAgentBranchChange = (option) => {
    dispatch(
      setPerformanceModals({
        key: 'newContacts',
        subkey: 'searchStatus',
        value: option.value,
      })
    );
    track.newContacts(TRACK_ELEMENT.DROPDOWN_SHOW_ALL);
  };

  return (
    <BaseModal
      id="new-contacts-modal"
      title={Trans(translations, 'CountNewContacts', String(newContacts.count))}
      handleClose={() =>
        dispatch(
          setPerformanceModals({
            key: 'newContacts',
            subkey: 'isOpen',
            value: false,
          })
        )
      }
    >
      <Box className={styles.modalControls}>
        <SearchInput
          modal="newContacts"
          dropdownOptions={contactTypesOptions}
          maxWidth={552}
        />

        <Box className={styles.dropdownWrapper}>
          <Text className={styles.dropdownWrapperLabel}>
            {Trans(translations, 'NewContactsTable.Show')}
          </Text>
          <TableDropdown
            selectedOption={selectedStatusOption}
            options={contactStatusOptions}
            onSelect={(option) => handleAgentBranchChange(option)}
          />
        </Box>

        <DownloadButton
          onClick={handleDownloadExcel}
          disabled={isDownloading}
        />
      </Box>
      {searchQuery && list?.length === 0 && !loading ? (
        <TableSearchEmptyState onClearFilters={handleClearFilters} />
      ) : (
        <BaseModal.TableContainer>
          <NewContactsTable />
        </BaseModal.TableContainer>
      )}
    </BaseModal>
  );
}
